'use client'

import { forwardRef, useCallback, useImperativeHandle, useRef, type HTMLAttributes, type MouseEvent } from 'react'
import { motion, useAnimation, useReducedMotion } from 'framer-motion'

/* @animateicons/react has no cake in its set, so this one is hand-rolled on the
   Lucide `cake` outline. Props + imperative handle mirror the package exactly
   (size / duration / isAnimated / color, startAnimation / stopAnimation), so it
   drops into the same call sites as GiftIcon and CameraIcon. */

export interface AnimatedIconHandle {
  startAnimation: () => void
  stopAnimation:  () => void
}

export type AnimatedIconProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'color' | 'onDrag' | 'onDragStart' | 'onDragEnd' | 'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration'
> & {
  size?:       number
  duration?:   number
  isAnimated?: boolean
  color?:      string
}

/** The three candle flames, in draw order — index drives the flicker stagger. */
const FLAMES = [7, 12, 17]

const AnimatedCakeIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ onMouseEnter, onMouseLeave, className, size = 24, duration = 1, isAnimated = true, color, style, ...rest }, ref) => {
    const body     = useAnimation()
    const flames   = useAnimation()
    const reduced  = useReducedMotion()
    const hasRef   = useRef(false)

    const start = useCallback(() => {
      if (reduced) return
      body.start('animate')
      flames.start('animate')
    }, [body, flames, reduced])

    const stop = useCallback(() => {
      body.start('rest')
      flames.start('rest')
    }, [body, flames])

    useImperativeHandle(ref, () => {
      hasRef.current = true
      return { startAnimation: start, stopAnimation: stop }
    })

    /* With a ref attached the parent owns the hover (so the whole menu row can
       trigger it); standalone, the icon animates on its own hover. */
    const handleEnter = useCallback((e: MouseEvent<HTMLDivElement>) => {
      if (!isAnimated || reduced) return
      if (hasRef.current) onMouseEnter?.(e)
      else start()
    }, [isAnimated, reduced, onMouseEnter, start])

    const handleLeave = useCallback((e: MouseEvent<HTMLDivElement>) => {
      if (hasRef.current) onMouseLeave?.(e)
      else stop()
    }, [onMouseLeave, stop])

    return (
      <motion.div
        className={`inline-flex items-center justify-center ${className ?? ''}`}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        animate={body}
        initial="rest"
        variants={{
          rest:    { y: 0 },
          animate: { y: [0, -1.5, 0], transition: { duration: 0.55 * duration, ease: 'easeInOut' } },
        }}
        style={{ color, ...style }}
        {...rest}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8" />
          <path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2.5 2 4 2 2-1 2-1" />
          <path d="M2 21h20" />
          <path d="M7 8v3" />
          <path d="M12 8v3" />
          <path d="M17 8v3" />
          {FLAMES.map((x, i) => (
            <motion.circle
              key={x}
              cx={x}
              cy="4"
              r="1"
              fill="currentColor"
              stroke="none"
              animate={flames}
              initial="rest"
              variants={{
                rest:    { scale: 1, opacity: 1, y: 0 },
                animate: {
                  scale:   [1, 1.45, 0.9, 1.25, 1],
                  opacity: [1, 0.75, 1, 0.85, 1],
                  y:       [0, -0.6, 0.2, -0.4, 0],
                  transition: {
                    duration: 0.9 * duration,
                    ease:     'easeInOut',
                    delay:    i * 0.09 * duration,
                  },
                },
              }}
              style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
            />
          ))}
        </svg>
      </motion.div>
    )
  },
)

AnimatedCakeIcon.displayName = 'AnimatedCakeIcon'

export default AnimatedCakeIcon
